import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '@jmvilaca-corp/auth-ui'

export const standardSource = 'standard'

export const sportCoricoSource = 'sportcorico'

/** Type de calendrier propose a la creation : standard, ou lie a une autre application. */
export interface CalendarSource {
  key: string
  label: string
  /** L'utilisateur doit avoir lie son compte chez ce fournisseur. */
  requiresAccount: boolean
  /** Identifiant du compte deja lie, sinon `null`. */
  login: string | null
}

export type ConnectionState = 'connected' | 'rejected' | 'unavailable' | 'notLinked'

export interface ConnectionStatus {
  state: ConnectionState
  login: string | null
}

const sourcesKey = ['calendar-sources'] as const
export const statusKey = (connector: string) => ['connectors', connector, 'status'] as const

export function useCalendarSources() {
  return useQuery({ queryKey: sourcesKey, queryFn: () => apiGet<CalendarSource[]>('/api/calendar-sources') })
}

/** Lie le compte de l'utilisateur chez le fournisseur ; le serveur verifie les identifiants avant de les enregistrer. */
export function useLinkConnector(connector: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (credentials: { login: string; password: string }) =>
      apiSend<ConnectionStatus>(`/api/connectors/${connector}/account`, 'PUT', credentials),
    onSuccess: (status) => {
      queryClient.setQueryData(statusKey(connector), status)
      return queryClient.invalidateQueries({ queryKey: sourcesKey })
    },
  })
}

/**
 * Le serveur rejoue les identifiants enregistres aupres du fournisseur. Une
 * fois par ouverture de session, pas a chaque navigation : chaque appel est
 * une vraie connexion chez le fournisseur.
 */
export function connectorStatusOptions(connector: string) {
  return queryOptions({
    queryKey: statusKey(connector),
    queryFn: () => apiGet<ConnectionStatus>(`/api/connectors/${connector}/status`),
    staleTime: Infinity,
  })
}

export function useConnectorStatus(connector: string) {
  return useQuery(connectorStatusOptions(connector))
}
